import { useState } from 'react';
import * as React from 'react';

import {
  Alert, Button, DialogContent, Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';

import { SubscriptionsService } from '@klangio/ai2notes-ts-client';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import type { WithUser } from '../../../context';
import { withUserContext } from '../../../context';

import WavyDialogBackground from '../../atoms/WavyDialogBackground';

const useStyles = makeStyles()(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1em',
    maxWidth: '30em',
  },
  subtext: {
    margin: '1em 0',
  },
  gap: {
    margin: '1em 0',
  },
  buttons: {
    display: 'flex',
    gap: '1em',
  },
}));

export interface CancelSubscriptionOfferProps extends WithUser {
  open: boolean;
  onClose: () => void;
  /** Use the subscription provider id for paddle transcriptions */
  providerId: string;
  onError: () => void;
  onSubmit: () => void;
}

const TOO_EXPENSIVE_DISCOUNT = 60;

const CancelSubscriptionOffer: React.FC<CancelSubscriptionOfferProps> = ({
  open,
  onClose,
  onError,
  onSubmit,
  providerId,
  refresh,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [discountSuccess, setDiscountSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const handleDiscount = async () => {
    try {
      setSubmitting(true);
      await SubscriptionsService.subscriptionsAddDiscount({
        subscriptionId: providerId,
        discountAmount: TOO_EXPENSIVE_DISCOUNT,
      });
      setTimeout(() => refresh(), 3000);
      setDiscountSuccess(true);
      setSubmitting(false);
    } catch {
      onError();
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    setSubmitting(true);
    onSubmit();
  };

  if (discountSuccess) {
    return (
      <Dialog open={open} onClose={onClose}>
        <WavyDialogBackground />
        <DialogContent className={classes.content}>
          <div className={classes.gap}>
            <Alert>{t('dialog.cancel_subscription.too_expensive_dialog.discount_success')}</Alert>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Typography variant="h5" align="center">
        <strong>
          {t('dialog.cancel_subscription.too_expensive_dialog.discount_text', {
            discount: `${TOO_EXPENSIVE_DISCOUNT}%`,
          })}
        </strong>
      </Typography>
      <div className={classes.subtext}>
        <Typography variant="body1" fontSize="small" align="center">
          {t('dialog.cancel_subscription.too_expensive_dialog.description_text', {
            discount: `${TOO_EXPENSIVE_DISCOUNT}%`,
          })}
        </Typography>
      </div>
      <div className={classes.buttons}>
        <Button
          id="btn_cancellation_discount_redeemed"
          onClick={handleDiscount}
          variant="contained"
          disabled={submitting}
        >
          {t('dialog.cancel_subscription.too_expensive_dialog.stay_button')}
        </Button>
        <Button
          id="btn_cancellation_discount_declined"
          disabled={submitting}
          onClick={handleSubmit}
          variant="outlined"
          color="primary"
        >
          {t('dialog.cancel_subscription.too_expensive_dialog.cancel_button')}
        </Button>
      </div>
    </>
  );
};

export default withUserContext(CancelSubscriptionOffer);
